'use client';

import { Box, Button, FormHelperText, IconButton, InputBase, Stack, TextField, TextFieldProps, Tooltip } from "@mui/material";
import { ComponentRef, forwardRef, useMemo, useState } from "react";
import { LinkEditable, LinkSchema } from "@/app/features/shared/__to_be_integrated/types/forms/LinkSchema";
import { Enum_Componentlinkslink_Type } from "@/app/lib/generated/graphql-types";
import Typography from "@mui/material/Typography";
import { FormState } from "@/app/features/shared/form-builder/types/types";
import { defaultFormState } from "@/app/features/shared/form-builder/types/defaults";
import { fieldErrorResolver } from "@/app/features/shared/__to_be_integrated/types/forms/fieldErrorResolver";
import { Add, Delete, InfoOutlined } from "@mui/icons-material";

export type LinkEditorProps<T extends LinkEditable | LinkEditable[]> = {
    value?: T | null;
    onChange: (value: T | undefined) => void;
    multi?: boolean;
    rowPaddingTop?: number;
}

export default function LinkEditor<T extends LinkEditable | LinkEditable[]>({
    value,
    multi = false,
    onChange,
    rowPaddingTop = 1,
}: LinkEditorProps<T>) {
    const isMultiple = Array.isArray(value) || (!value && multi);

    const addNewLink = () => {
        if (!isMultiple) return;
        onChange([
            ...value || [],
            emptyLink as LinkEditable
        ] as T)
    }

    const deleteLink = (index: number) => {
        if (!isMultiple) return;
        onChange((value || []).filter((_, i) => i !== index) as T);
    }

    function onLinkChangeSingle(linkValue: LinkEditable) {
        if (isMultiple) return;
        onChange?.(linkValue as T)
    }

    function onLinkChangeMulti(linkValue: LinkEditable, index: number) {
        if (!isMultiple) return;
        onChange(
            value?.map((v, i) =>
                i === index ? linkValue : v
            ) as T
        )
    }

    return (
        <Box sx={{ display: 'flex', flex: '0 1 auto' }}>
            {isMultiple
                ? (
                    <Stack sx={{ display: 'flex', flex: '1 1 auto' }}>
                        {value?.map((link, index) =>
                            <EditorRow
                                key={link.id || `${link.name}-${index}`}
                                initialData={link}
                                rowPaddingTop={rowPaddingTop}
                                onChange={(value) =>
                                    onLinkChangeMulti(value, index)
                                }
                                onDelete={() => deleteLink(index)}
                                autoFocus={false}
                            />
                        )}
                        <Button
                            variant="outlined"
                            color="breakout"
                            startIcon={<Add />}
                            sx={{ mt: 1 }}
                            onClick={addNewLink}
                        >
                            Link Hinzufügen
                        </Button>
                    </Stack>
                )
                : (
                    <>
                        {!value
                            ? (
                                <Button variant="outlined" color="breakout" fullWidth onClick={() => onChange(emptyLink as T)} sx={{ flexGrow: 0 }}>Link Hinzufügen</Button>
                            )
                            : (
                                <EditorRow
                                    initialData={value} onChange={onLinkChangeSingle}
                                    onDelete={() => onChange(undefined)}
                                    autoFocus={false}
                                    rowPaddingTop={rowPaddingTop}
                                />
                            )
                        }
                    </>
                )
            }
        </Box>
    )
}

function EditorRow({
    initialData,
    onDelete,
    onChange,
    autoFocus = false,
    rowPaddingTop = 1,
}: {
    initialData: LinkEditable,
    onDelete?: () => void,
    onChange: (value: LinkEditable) => void,
    autoFocus?: boolean,
    rowPaddingTop?: number,
}) {

    const [nameDirty, setNameDirty] = useState(false);
    const [urlDirty, setUrlDirty] = useState(false);
    const validation = useMemo(() => LinkSchema.safeParse(initialData), [initialData]);
    const formState = useMemo<FormState>(() => ({
        ...defaultFormState,
        isValid: validation.success,
        fieldErrors: validation.success ? null : validation.error.issues,
    }), [validation]);

    const nameErrorResolver = useMemo(() =>
        fieldErrorResolver("name", formState), [formState]
    );

    const urlErrorResolver = useMemo(() =>
        fieldErrorResolver("url", formState), [formState]
    );
    const showValidationErrors = nameDirty && urlDirty;

    const handleNameChange = (name: string) => {
        setNameDirty(true);
        updateFormData({ name });
    };

    const handleUrlChange = (url: string) => {
        setUrlDirty(true);
        updateFormData({ url });
    };

    const updateFormData = (partialFormData: Partial<LinkEditable>) => {
        const nextUrl = partialFormData.url ?? initialData.url ?? '';
        const isInternal = isInternalUrl(nextUrl);
        const finalUrl = nextUrl.replace(window.location.origin, '');
        const nextValue = {
            ...initialData,
            ...partialFormData,
            url: finalUrl,
            type: isInternal == true ? Enum_Componentlinkslink_Type.Internal : Enum_Componentlinkslink_Type.External
        };
        onChange(nextValue);
    }

    return (
        <Stack direction="row" spacing={2} sx={{ display: 'flex', flex: '1 1 auto', height: 'fit-content', pt: rowPaddingTop, alignItems: 'flex-start' }}>
            <CombinedLinkField
                name={initialData.name}
                url={initialData.url}
                onNameChange={handleNameChange}
                onUrlChange={handleUrlChange}
                nameError={showValidationErrors ? nameErrorResolver.hasError : false}
                nameHelperText={showValidationErrors ? nameErrorResolver.error : ""}
                urlError={showValidationErrors ? urlErrorResolver.hasError : false}
                urlHelperText={showValidationErrors ? urlErrorResolver.error : ""}
                fullWidth
                autoFocus={autoFocus}
            />
            {onDelete && (
                <IconButton
                    onClick={onDelete}
                    aria-label="Link entfernen"
                    size="small"
                    sx={{ width: 40, height: 40, alignSelf: 'flex-start' }}
                >
                    <Delete color="error" sx={{ fontSize: 28 }} />
                </IconButton>
            )}
        </Stack>
    )
}

const URLTextField = forwardRef<
    ComponentRef<typeof TextField>,
    TextFieldProps
>(
    ({ children, ...rest }, ref) => {
        const currentDomain = window.location.origin;
        const currentValue =  rest.value as string | undefined;
        const isInternal = isInternalUrl(currentValue);

        if (currentValue?.startsWith(currentDomain)) {
            rest.value = currentValue?.replace(`${currentDomain}`, '');
        }

        return (
            <TextField
                ref={ref}
                slotProps={{
                    input: {
                        endAdornment: (isInternal !== undefined && (isInternal
                            ? (
                                <Box
                                    sx={{
                                        height: '100%',
                                        alignContent:'center',
                                        backgroundColor: (theme) => theme.palette.background.default
                                    }}
                                >
                                    <Typography
                                        textAlign="left"
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: '8px',
                                            height: '100%',
                                            alignContent:'flex-start',
                                            alignItems:'center',
                                            px: 2,
                                        }}
                                    >
                                        Intern
                                    </Typography>
                                </Box>
                            ) : (
                                <Tooltip
                                    title={<Typography>Externe Links werden immer in einem neuen Tab geöffnet</Typography>}
                                    sx={{
                                        display: 'flex',
                                        height: '100%',
                                        ml: 1,
                                        backgroundColor: (theme) => theme.palette.background.default
                                    }}
                                >
                                    <Stack direction="row" spacing={1}>

                                    <Typography
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: '8px',
                                            height: '100%',
                                            alignContent:'center',
                                            alignItems:'center',
                                            px: 2,
                                        }}
                                    >
                                        Extern
                                        <InfoOutlined sx={{ fontSize: 18}} />
                                    </Typography>
                                    </Stack>
                                </Tooltip>
                            )
                            )
                        )
                    }
                }}
                sx={{
                    '& .MuiInputBase-adornedEnd': {
                        pr: 0
                    }
                }}
                {...rest}
            >
                {children}
            </TextField>
        )
    }
);
URLTextField.displayName = "URLTextField";

function CombinedLinkField({
    name,
    url,
    onNameChange,
    onUrlChange,
    nameError,
    nameHelperText,
    urlError,
    urlHelperText,
    fullWidth,
    autoFocus,
}: {
    name?: string | null;
    url?: string | null;
    onNameChange: (value: string) => void;
    onUrlChange: (value: string) => void;
    nameError?: boolean;
    nameHelperText?: string | null;
    urlError?: boolean;
    urlHelperText?: string | null;
    fullWidth?: boolean;
    autoFocus?: boolean;
}) {
    const hasError = Boolean(nameError || urlError);
    const helperText = [nameHelperText, urlHelperText].filter(Boolean).join(" ");

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: fullWidth ? "1 1 auto" : undefined,  }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid",
                    borderColor: hasError ? "error.main" : 'divider',
                    borderRadius: 1,
                    minHeight: 40,
                    px: 1.5,
                    "&:focus-within": {
                        borderColor: hasError ? "error.main" : "primary.main",
                        boxShadow: (theme) =>
                            hasError ? `0 0 0 1px ${theme.palette.error.main}` : `0 0 0 1px ${theme.palette.primary.main}`,
                    },
                }}
            >
                <InputBase
                    value={name ?? ""}
                    onChange={(e) => onNameChange(e.target.value)}
                    placeholder="Anzeigetext"
                    inputProps={{ "aria-label": "Anzeigetext" }}
                    autoFocus={autoFocus}
                    sx={{ flex: "0 0 38%" }}
                />
                <Box sx={{ display: "flex", flex: "1 1 auto", borderLeft: "1px solid", borderColor: "divider", pl: 1.5, ml: 1.5 }}>
                    <URLTextField
                        size="small"
                        value={url ?? ""}
                        onChange={(e) => onUrlChange(e.target.value)}
                        placeholder="URL"
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        fullWidth
                    />
                </Box>
            </Box>
            {helperText ? (
                <FormHelperText error={hasError} sx={{ mx: 1.5 }}>
                    {helperText}
                </FormHelperText>
            ) : null}
        </Box>
    );
}

const emptyLink: LinkEditable = {
    id: 'new',
    name: '',
    url: '',
    type: Enum_Componentlinkslink_Type.Internal,
}

const isInternalUrl = (url?: string): boolean | undefined => {
    if (!url || url === '') return undefined;
    return url.startsWith('/') || url.startsWith(window.location.origin);
}
